import React, { useEffect, useState } from 'react';
import { Avatar, Box, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { ChatBubbleOutline, EventAvailable, Favorite, Forum, Groups2, LocationOn, MenuBook, People } from '@mui/icons-material';
import SignInNudge from './SignInNudge';
import { format } from 'date-fns';
import { postService, PublicPostHighlight } from '../services/postService';
import { publicService, PublicCommunity } from '../services/publicService';
import { authService } from '../services/authService';

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

const PILLARS = [
  { icon: <Forum color="primary" />, title: 'Share your dives', body: 'Post dives and photos to a feed your buddies actually follow.' },
  { icon: <Groups2 color="primary" />, title: 'Find your people', body: 'Meet divers, join local groups and clubs, and message each other.' },
  { icon: <EventAvailable color="primary" />, title: 'Plan together', body: 'Schedule dives and trips, and invite the crew with one tap.' },
  { icon: <MenuBook color="primary" />, title: 'Log it all', body: 'Your dive log, sites and gear tracking are built in — import from Suunto or enter by hand.' },
];

function previewText(post: PublicPostHighlight): string {
  return post.text || (post.type === 'DiveLog' ? post.diveLabel : post.type === 'EquipmentAdded' ? post.equipmentLabel : post.type === 'CheckIn' && post.diveSiteName ? `Diving at ${post.diveSiteName}` : post.type === 'ConditionsReport' && post.diveSiteName ? `Conditions at ${post.diveSiteName}` : null) || 'Shared a post';
}

/** Signed-out home page: shows what the community is up to, then invites the visitor in. */
const GuestLanding: React.FC = () => {
  const [posts, setPosts] = useState<PublicPostHighlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [community, setCommunity] = useState<PublicCommunity | null>(null);

  useEffect(() => {
    let cancelled = false;
    publicService.getCommunity()
      .then(c => { if (!cancelled) setCommunity(c); })
      .catch(() => { if (!cancelled) setCommunity(null); });
    postService.getPublicHighlights()
      .then(results => { if (!cancelled) setPosts(results); })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 0, sm: 2 } }}>
      <Box sx={{ textAlign: 'center', pt: { xs: 1, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.75rem' }, lineHeight: 1.15 }}>
          Where divers connect
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mt: 1.5, fontWeight: 400 }}>
          Share dives, find buddies, join clubs, and plan your next trip — together.
        </Typography>
      </Box>

      <SignInNudge
        feature="join the community"
        description="Creating a free account only takes a moment. Your feed, buddies, groups, and dive log will be waiting for you."
        onSignedIn={() => authService.getUser()}
      />

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 4 }}>
        {PILLARS.map(pillar => (
          <Paper key={pillar.title} variant="outlined" sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            {pillar.icon}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{pillar.title}</Typography>
              <Typography variant="body2" color="text.secondary">{pillar.body}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {community && community.events.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Coming up</Typography>
          <Stack spacing={1.5}>
            {community.events.map((ev, i) => (
              <Paper key={`${ev.title}-${ev.startTime}-${i}`} variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{ev.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(ev.startTime), 'EEE, MMM d · h:mm a')}{ev.hostedBy ? ` · ${ev.hostedBy}` : ''}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 0.75 }}>
                  {ev.location && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn sx={{ fontSize: 14 }} /> {ev.location}
                    </Typography>
                  )}
                  {ev.goingCount > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <People sx={{ fontSize: 14 }} /> {ev.goingCount} going
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {community && (community.groups.length > 0 || community.clubs.length > 0) && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Groups and clubs to join</Typography>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {[...community.clubs.map(c => ({ ...c, kind: 'Club' })), ...community.groups.map(g => ({ ...g, kind: 'Group' }))].map((item, i) => (
              <Paper key={`${item.kind}-${item.name}-${i}`} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.name}</Typography>
                  <Chip size="small" variant="outlined" label={item.kind} />
                </Stack>
                {item.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>{item.description}</Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {item.memberCount} member{item.memberCount === 1 ? '' : 's'}
                </Typography>
              </Paper>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Create a free account to join in.
          </Typography>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      )}

      {posts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>Happening in the community</Typography>
          <Stack spacing={1.5}>
            {posts.map(post => (
              <Paper key={post.postId} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                  <Avatar
                    src={post.authorProfilePhotoUrl ?? undefined}
                    sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 13, fontWeight: 700 }}
                  >
                    {!post.authorProfilePhotoUrl && (initials(post.authorDisplayName) || '?')}
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{post.authorDisplayName}</Typography>
                </Stack>
                <Typography variant="body2" sx={{ mb: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {previewText(post)}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Chip size="small" variant="outlined" icon={<Favorite sx={{ fontSize: 14 }} />} label={post.reactionCount} />
                  <Chip size="small" variant="outlined" icon={<ChatBubbleOutline sx={{ fontSize: 14 }} />} label={post.commentCount} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default GuestLanding;
